import { z } from "zod";
import { IsoDateTime, OpaqueId, Position, Seq } from "./common.js";
/**
 * Type de position — distinction de fiabilité exigée par le CDC §3.1, non négociable,
 * calculée par le backend et non recontournable côté frontend (D-001).
 */
export const PositionType = z.enum([
    "gps", // position réelle rapportée par le véhicule sur ce cycle
    "estimated", // pas de GPS frais, mais un passage temps réel permet d'interpoler
    "scheduled", // aucune donnée temps réel — position déduite du seul horaire, interpolée le long du tracé
    "stale", // terminal figé alors que la course continue — position déclassée
]);
export const RunConfidence = z.enum(["established", "tentative"]).nullable();
export const NextStop = z.object({
    stopId: OpaqueId,
    name: z.string(),
    sequence: z.number().int(),
    scheduled: IsoDateTime, // fuseau DE L'ARRÊT (CDC §3.2)
    expected: IsoDateTime.nullable(),
    skipped: z.boolean(),
});
export const ObservedStop = z.object({
    stopId: OpaqueId,
    name: z.string(),
    sequence: z.number().int(),
    scheduled: IsoDateTime,
    observed: IsoDateTime,
});
export const Vehicle = z.object({
    id: OpaqueId, // identité réelle, unique réseau (CDC §6.1) — JAMAIS le numéro de parc
    displayNumber: z.string(), // numéro de parc — AFFICHAGE SEUL
    operatorId: z.string(),
});
export const Journey = z.object({
    id: OpaqueId,
    lineId: OpaqueId,
    direction: z.union([z.literal(0), z.literal(1)]),
    headsign: z.string(),
    // null pour une course purement théorique dont aucun véhicule n'est encore
    // identifié en temps réel (CDC §3.2 — la course apparaît quand même).
    vehicle: Vehicle.nullable(),
    position: Position,
    positionType: PositionType,
    positionAt: IsoDateTime,
    deviationSeconds: z.number().int(), // >0 retard, <0 avance
    // D-018 — surcouche carte « Fréquence ». Intervalle RÉEL (s) écoulé depuis le
    // passage du véhicule précédent de la même ligne / même sens, mesuré à leur
    // prochain arrêt commun. `null` : véhicule de tête, ou pas d'arrêt commun de
    // référence (patrons d'arrêts distincts, course ajoutée).
    headwaySeconds: z.number().int().nonnegative().nullable(),
    // D-018 — intervalle CIBLE (s) de la ligne à cet instant : la fréquence
    // prévue au tableau de marche (médiane des écarts théoriques entre courses
    // consécutives du sens), PAS « l'écart à la prochaine course théorique » (qui
    // serait faussé si une sortie manque). `null` si indéterminable (< 2 courses).
    scheduledHeadwaySeconds: z.number().int().positive().nullable(),
    occupancy: z.number().min(0).max(1).nullable(), // D-005 — null tant que la source n'est pas confirmée
    depotId: OpaqueId.nullable(), // D-007 — toujours null, aucune source
    runId: z.string().nullable(), // « sortie » complète « T3-04 » si rapprochée (CDC §5.8)
    // N° de sortie **nu** (« 04 »), extrait de `runId` — pour l'afficher sans le
    // code ligne (déjà porté par l'icône). `null` = course non rapprochée : le
    // front affiche « ? ».
    runNumber: z.string().nullable(),
    serviceId: z.string().nullable(), // n° de service — désignation de service, JAMAIS d'agent
    runConfidence: RunConfidence,
    addedTrip: z.boolean(), // course absente du théorique (CDC §3.2)
    /**
     * D-022 — véhicule **hors course commerciale** : rien à réguler dessus
     * (glyphe + libellé noirs côté carte). Deux cas, tous deux établis sur des
     * faits, jamais devinés :
     *  - v1.47 — **retour dépôt confirmé** : la dernière course de la marche de
     *    sortie est terminée, l'heure de Rentrée n'est pas atteinte ;
     *  - v1.55 — **course terminée, véhicule toujours positionné** : la fenêtre
     *    théorique de sa dernière course est dépassée, Cityway a retiré le
     *    TripUpdate (il le fait dès la fin) mais continue de publier la position
     *    du véhicule sur ce `trip_id` pendant ~2 h 30 (mesuré 2026-09-18 : 0 sur
     *    55 de ces courses avaient encore un TripUpdate). Battement au terminus
     *    ou HLP — dans les deux cas, pas une course. La course porte alors
     *    `lineId` de la dernière course, `headsign` vide, `nextStops: []`,
     *    `deviationSeconds: 0`, et `runId` si le véhicule est rapproché.
     * `false` par défaut / si non déterminable.
     */
    hlp: z.boolean().default(false),
    nextStops: z.array(NextStop),
});
/**
 * N° de sortie nu à partir de `runId` (« F7-14 » → « 14 », « T3-04 » → « 04 »).
 * Le code ligne ne contient jamais de tiret. `null` si `runId` absent.
 */
export function runNumberOf(runId) {
    if (!runId)
        return null;
    const i = runId.indexOf("-");
    return i >= 0 ? runId.slice(i + 1) : runId;
}
/** `GET /networks/{n}/journeys/{id}` — détail : nextStops complet + observedStops. */
export const JourneyDetail = Journey.extend({
    observedStops: z.array(ObservedStop),
});
export const JourneysResponse = z.object({
    at: IsoDateTime,
    seq: Seq, // D-009 — seq de cet instantané ; le flux applique les deltas au-delà
    truncated: z.boolean(), // true si le cap dur de 2000 courses est atteint
    journeys: z.array(Journey),
});
//# sourceMappingURL=journey.js.map